import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RIGHT_DRAWER } from '@flexo/reducers';
import { useLocation } from 'react-router-dom';
import styles from './right-drawer-content.module.scss';
import {
  formatWeek,
  formatDay,
  formatMonth,
  formatYear,
  checkItIsToday,
} from '../../../../../organisms/src/lib/hive-calendar/helpers';
import { formatToMaxFourDigits } from '@flexo/general';
import { DrawerCloseButton } from '../drawer-close-button';
import React from 'react';

interface DrawerConfig {
  titleKey?: string;
  descriptionKey?: string;
}

interface CommunitySelfSufficiencyDrawerContentProps {
  drawerConfig?: DrawerConfig;
}

/**
 * FLEXO-6066 detail drawer: Community Self-sufficiency.
 * Mirrors CommunityTotalEstimatedBenefitsDrawerContent: reads the already-loaded
 * energy KPI (`self_sufficiency_rate`) for the selected entity. Entity-aware
 * (community / member / site) so it can be reused.
 */
export const CommunitySelfSufficiencyDrawerContent: React.FC<
  CommunitySelfSufficiencyDrawerContentProps
> = ({ drawerConfig }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const location = useLocation();

  const pathSegment = location.pathname.split('/')[1];
  const {
    hiveCalendar: { selectedDate, selectedTimeSet },
  } = calendar;

  let entityType = 'site';
  if (pathSegment?.includes('member')) {
    entityType = 'member';
  } else if (pathSegment?.includes('community')) {
    entityType = 'community';
  }

  const config: any = {
    community: { getName: (e: any) => e?.communityName, entity: 'Community' },
    member: {
      getName: (e: any) =>
        `${e?.memberFirstName || ''} ${e?.memberLastName || ''}`.trim(),
      entity: 'Member',
    },
    site: { getName: (e: any) => e?.pod, entity: 'Site' },
  }[entityType];

  const overviewKpiApiData =
    (kpi[`Energy${config.entity}`]?.['1day']?.[0]?.kpi as any) || null;
  const entity = metadata?.selectedEntity || {};

  function getKpi(kpiName: string) {
    const k = (overviewKpiApiData || []).find((x: any) => x.name === kpiName);
    if (!k) return '-.-';
    return `${formatToMaxFourDigits(k.value)} ${k.unit || ''}`.trim();
  }

  function closeDrawer() {
    dispatch(RIGHT_DRAWER.HideDrawer());
  }

  const getDate = () => {
    let result = '';
    switch (selectedTimeSet) {
      case 'day':
        if (checkItIsToday(selectedDate)) result = t('widgets.calendar.today');
        else result = formatDay(selectedDate);
        break;
      case 'week':
        result = selectedDate[0] ? `${formatWeek(selectedDate)}` : 'Select Week';
        break;
      case 'month':
        result = formatMonth(selectedDate);
        break;
      case 'year':
        result = formatYear(selectedDate);
        break;
      default:
        result = formatDay(selectedDate);
        break;
    }
    return result;
  };

  return (
    <div className={styles.RightDrawerContent__Wrapper}>
      <div className={styles.RightDrawerContent__Title}>
        <p className={styles.RightDrawerContent__Paragraph}>
          {t(
            drawerConfig?.titleKey ||
              'widgets.selfSufficiencyCardCommunity.details.title'
          )}
        </p>
        <DrawerCloseButton onClose={closeDrawer} ariaLabel={t('common.close', { defaultValue: 'Close' })} />
      </div>
      <div className={styles.RightDrawerContent__Subtitle}>
        <p className={styles.RightDrawerContent__Paragraph}>
          {config.getName(entity)} <label>{getDate()}</label>
        </p>
      </div>
      <div className={styles.RightDrawerContent__Body}>
        <p className={styles.RightDrawerContent__Body__Value}>
          {getKpi('self_sufficiency_rate')}
        </p>
        <p className={styles.RightDrawerContent__Body__Description}>
          {t(
            drawerConfig?.descriptionKey ||
              'widgets.selfSufficiencyCardCommunity.details.label'
          )}
        </p>
      </div>
    </div>
  );
};

export default CommunitySelfSufficiencyDrawerContent;
